import { useEffect, useState } from "react";
import InstallPWAButton from "./InstallPWAButton";

/**
 * Small "Download the App" pill — sits above the first engine card.
 * Behavior by device:
 *   - Mobile (Android / iOS): renders the existing InstallPWAButton
 *     (Android = native prompt, iOS = "Add to Home Screen" sheet).
 *   - Already installed (running standalone): renders nothing.
 *   - Desktop: renders a tiny one-line hint ("Open this page on
 *     your phone to install") so the user / page-owner can confirm
 *     the CTA is wired up; nothing is interactive on desktop, since
 *     PWAs cannot be installed there in any meaningful sense.
 */
export default function InstallAppPill() {
  const [device, setDevice] = useState("idle"); // idle | mobile | desktop | standalone

  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    const isMobile = /android|iphone|ipad|ipod|opera mini|iemobile|mobile/i.test(ua);
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setDevice("standalone");
    } else if (isMobile) {
      setDevice("mobile");
    } else {
      setDevice("desktop");
    }
  }, []);

  if (device === "standalone") return null;

  return (
    <div
      className="flex items-center justify-center mt-4 mb-6"
      data-testid="install-app-pill"
    >
      {device === "mobile" ? (
        <InstallPWAButton />
      ) : (
        <span
          className="inline-flex items-center gap-2 font-cinzel text-[10px] sm:text-xs tracking-[0.24em] uppercase text-[#FFE7A0]/85 border border-[#FFE7A0]/30 rounded-full px-4 py-2 bg-[#03060e]/45 backdrop-blur-[2px]"
          data-testid="install-app-pill-desktop"
          style={{ textShadow: "0 0 12px rgba(5,8,24,0.95)" }}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download the app &middot; open on your phone
        </span>
      )}
    </div>
  );
}
